import { RefObject, useEffect, useState } from 'react'

export interface ScrollEdges {
  /** Content hidden to the left (the box has been scrolled). */
  left: boolean
  /** Content hidden to the right. */
  right: boolean
}

/** Which edges of a horizontally scrollable box hide content. 1px of slack for sub-pixel widths. */
export function scrollEdges(scrollLeft: number, scrollWidth: number, clientWidth: number): ScrollEdges {
  const hidden = scrollWidth - clientWidth
  if (hidden <= 1) return { left: false, right: false }
  return { left: scrollLeft > 1, right: scrollLeft < hidden - 1 }
}

/**
 * Tracks which edges of a horizontally scrollable element hide content, so a
 * table can show a fade there — macOS hides scrollbars until you scroll, and
 * a cut-off column otherwise just looks broken.
 *
 * Re-checks on scroll and whenever the element or its content changes size
 * (rows loading, window resizing).
 */
export function useScrollEdges(ref: RefObject<HTMLElement | null>): ScrollEdges {
  const [edges, setEdges] = useState<ScrollEdges>({ left: false, right: false })

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => {
      const next = scrollEdges(el.scrollLeft, el.scrollWidth, el.clientWidth)
      setEdges((prev) => (prev.left === next.left && prev.right === next.right ? prev : next))
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    observer?.observe(el)
    if (el.firstElementChild) observer?.observe(el.firstElementChild)
    return () => {
      el.removeEventListener('scroll', update)
      observer?.disconnect()
    }
  }, [ref])

  return edges
}
